import { useQuery } from '@tanstack/react-query';
import { useOperator } from '@/auth/use-operator';
import { getLockState } from '@/lib/locks/api';
import type { LockState } from '@/lib/locks/types';

// Reads the lock cache populated by:
//   - HTTP GET /v1/locks/:entityUrn  (initial fetch — useQuery)
//   - The central applyEvent merge on StateChanged events (live WS updates)
//
// staleTime: 0 per docs/ui/operator-ui-tds.md §6.2 — lock state must never
// serve stale. WS events update the cache live, so initial-fetch refetches
// only need to fire on focus / mount.

export interface LockView {
  state: LockState | undefined;
  isMine: boolean;
  isHeld: boolean;
  isAvailable: boolean;
  isLoading: boolean;
  isFetched: boolean;
}

export function useLockState(entityUrn: string): LockView {
  const { data: operator } = useOperator();
  const operatorUrn = operator?.operatorUrn ?? null;

  const query = useQuery<LockState>({
    queryKey: ['cases', entityUrn, 'lock'],
    queryFn: () => getLockState(entityUrn),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const state = query.data;
  const holder = state?.holder ?? null;
  const isHeld = holder !== null;
  const isMine = !!holder && operatorUrn !== null && holder.operatorUrn === operatorUrn;
  const isAvailable = !isHeld;

  return {
    state,
    isMine,
    isHeld,
    isAvailable,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
  };
}
